import apiClient from '@api/axios';
import { getPaginatedEndpointParams } from '@api/helpers';
import {
  IPaginatedResourceEndpointResponse,
  IUpsertResourceResponse,
} from '@api/interfaces';
import { FilterFields } from '@components/ManagedDataTable/interfaces';
import { AxiosResponse } from 'axios';
import {
  FeedMessage,
  FeedMessagesSort,
  ICommentParams,
  IPostFeedMessageParams,
  ResourceType,
} from './interfaces';
import { convertObjectToFormData } from '@helpers/apiHelper';
import { formatToQueryDateTimeValue } from '@helpers/dateHelpers';

export const getFeedMessages = async (
  resourceType: ResourceType,
  resourceId: number | string,
  page: number,
  pageSize: number,
  sort?: FeedMessagesSort,
  search?: string,
  filters?: FilterFields
): Promise<IPaginatedResourceEndpointResponse<FeedMessage>> => {
  const response: AxiosResponse<
    IPaginatedResourceEndpointResponse<FeedMessage>
  > = await apiClient.get(`/feed/${resourceType}/${resourceId}`, {
    params: getPaginatedEndpointParams({
      page,
      perPage: pageSize,
      sort,
      search,
      filters,
    }),
  });

  return response.data;
};

export const postFeedMessage = async (
  resourceType: ResourceType,
  resourceId: number | string,
  messageData: IPostFeedMessageParams
): Promise<null> => {
  const formData = convertObjectToFormData(messageData);

  const response: AxiosResponse<IUpsertResourceResponse> = await apiClient.post(
    `/feed/${resourceType}/${resourceId}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );

  return null;
};

export const deleteFeedMessage = async (messageId: number): Promise<void> => {
  const response: AxiosResponse<void> = await apiClient.delete(
    `/feed/messages/${messageId}`
  );

  return response.data;
};

export const getMessageComments = async (
  messageId: number,
  fromDate: Date,
  page: number,
  pageSize: number,
  sort?: FeedMessagesSort,
  search?: string,
  filters?: FilterFields
): Promise<IPaginatedResourceEndpointResponse<Comment>> => {
  const response: AxiosResponse<IPaginatedResourceEndpointResponse<Comment>> =
    await apiClient.get(`/feed/messages/${messageId}/comments`, {
      params: getPaginatedEndpointParams({
        page,
        perPage: pageSize,
        sort,
        search,
        filters: [
          ...(filters || []),
          {
            field: 'created_at',
            operator: '<',
            value: formatToQueryDateTimeValue(fromDate),
          },
        ],
      }),
    });

  return response.data;
};

export const postComment = async (
  messageId: number,
  commentData: ICommentParams
): Promise<null> => {
  const formData = convertObjectToFormData(commentData);

  const response: AxiosResponse<IUpsertResourceResponse> = await apiClient.post(
    `/feed/messages/${messageId}/comments`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );

  return null;
};

export const deleteComment = async (
  messageId: number,
  commentId: number
): Promise<void> => {
  await apiClient.delete(`/feed/messages/${messageId}/comments/${commentId}`);
};

export const postLike = async (messageId: number): Promise<void> => {
  await apiClient.post(`/feed/messages/${messageId}/likes`);
};

export const deleteLike = async (messageId: number): Promise<void> => {
  await apiClient.delete(`/feed/messages/${messageId}/likes`);
};

export const postCommentLike = async (
  messageId: number,
  commentId: number
): Promise<void> => {
  await apiClient.post(
    `/feed/messages/${messageId}/comments/${commentId}/likes`
  );
};

export const deleteCommentLike = async (
  messageId: number,
  commentId: number
): Promise<void> => {
  await apiClient.delete(
    `/feed/messages/${messageId}/comments/${commentId}/likes`
  );
};
